import { useMemo } from "react";
import { comboKey, type ModifierState, resolveActiveLayer } from "@/domain/profile-resolver/index";
import { projectLayers, stripDefaults } from "@/domain/profile-resolver/project";
import type {
  BoardLayer,
  Category,
  Keyboard,
  KeyboardLayout,
  Layers,
  Profile,
} from "@/domain/types";
import { parse } from "@/games/sc2/parser/index";
import { rapidFireMarks } from "@/games/sc2/render-aux";
import { charMapOf, qwerty } from "@/hardware/keyboard-layouts/index";
import { compose } from "@/render/board-compose/index";
import { composeMouse } from "@/render/mouse-map/compose";

// a stable empty layer set for a disabled side (the B side while compare is closed), so its memo
// keeps one identity across renders. Read-only everywhere downstream.
const EMPTY_LAYERS: Layers = { base: {} };

export interface SideRenderInput {
  /** This side's active character-space Profile, or null (an SC2 [Hotkeys]-only upload, a simple Game with none). */
  renderProfile: Profile | null;
  /** This side's Keyboard (the lens body). */
  board: Keyboard | undefined;
  /** This side's active mapping: a universal Keyboard Layout or a board layer. */
  keyboardLayout: KeyboardLayout | BoardLayer;
  /** This side's SC2 source text, parsed through the lens; null for a simple Game. */
  activeText: string | null;
  /** The Context id the projector resolves against. */
  contextId: string;
  /** The held / latched modifier state. */
  mods: ModifierState;
  categories: Record<string, Category>;
  /** Whether the rapid-fire overlay is on (SC2). */
  rapidFireOn: boolean;
  /** Hide bindings filled from the Standard table: show only what the player's file bound. */
  hideDefaults?: boolean;
  /**
   * Whether this side renders at all. The A side is always enabled; the compare B side only while
   * comparing, so the expensive parse / project / compose steps never run for a hidden B side - the
   * gate the two inline pipelines each used to carry by hand.
   */
  enabled: boolean;
}

/**
 * One side's full render pipeline: parse the source through the lens, project the
 * character-space Profile into the resolved layers for the active Context, resolve the held combo,
 * and compose keycaps + mouse zones. The visualizer drives this twice - the A side and the compare
 * B side - so the two stay provably identical instead of two hand-synced copies.
 */
export function useSideRender({
  renderProfile,
  board,
  keyboardLayout,
  activeText,
  contextId,
  mods,
  categories,
  rapidFireOn,
  hideDefaults,
  enabled,
}: SideRenderInput) {
  // the parse axis: the active mapping as a char table. Every SC2 side re-derives per Keyboard
  // Layout switch: an upload re-parses its character-space file through the new lens, and a
  // published Preset's activeText is itself re-served (converted) for the new lens upstream.
  const parseMap = useMemo(
    () => (enabled && board ? charMapOf(board, keyboardLayout) : qwerty),
    [enabled, board, keyboardLayout],
  );
  // the live decode through the lens; a char emitted from two keys keeps BOTH (twin-paint).
  const table = useMemo(
    () => (enabled && activeText !== null ? parse(activeText, parseMap) : null),
    [enabled, activeText, parseMap],
  );
  // the one projecting resolver: the character-space Profile through the lens at the
  // active Context. Memoized for stable layer identity.
  const layers = useMemo(() => {
    if (!(enabled && renderProfile && board)) return EMPTY_LAYERS;
    const projected = projectLayers(renderProfile, contextId, board, keyboardLayout);
    return hideDefaults ? stripDefaults(projected) : projected;
  }, [enabled, renderProfile, contextId, board, keyboardLayout, hideDefaults]);
  const activeModifierLayer = useMemo(() => resolveActiveLayer(layers, mods), [layers, mods]);
  // dim base-fallback caps only while a non-empty combo is held AND that combo binds something of
  // its own; otherwise every key sources to base and the whole board would dim.
  const comboHasExact =
    comboKey(mods) !== "" && Object.values(activeModifierLayer).some((r) => r.combo !== "");
  // rapid-fire marks derive from the parsed table alone; the toggle decision is made once here so
  // the board glyph, list chips, and readout tag can never desync.
  const rapidFire = useMemo(
    () => (enabled && table ? rapidFireMarks(table) : {}),
    [enabled, table],
  );
  const marks = rapidFireOn ? rapidFire : null;
  // the board shows the exact-combo set for the held modifiers (no fallback).
  const activeMarks = useMemo(() => marks?.[comboKey(mods)] ?? null, [marks, mods]);
  const keycaps = useMemo(
    () =>
      enabled && board
        ? compose(activeModifierLayer, board, keyboardLayout, categories, activeMarks)
        : [],
    [enabled, activeModifierLayer, board, keyboardLayout, categories, activeMarks],
  );
  // the mouse map is composed from the Profile alone: no Keyboard, no Keyboard Layout.
  const zones = useMemo(
    () => (enabled ? composeMouse(activeModifierLayer, categories) : []),
    [enabled, activeModifierLayer, categories],
  );
  return {
    parseMap,
    table,
    layers,
    activeModifierLayer,
    comboHasExact,
    marks,
    keycaps,
    zones,
  };
}
